import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import {
    AccountActions,
    AccountSelectors,
    AuthorizationActions,
    AuthorizationSelectors,
    LocalizationSelectors,
    ModulesSelectors,
    NavigationSelectors,
    SosLoaderService,
    TrackingActions,
    ValidationsSelectors,
    ValidationsService
} from '@serviceos-ng/core';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'serviceos-web-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
    @Select(AuthorizationSelectors.getSocialProviders)
    public socialProviders$!: Observable<any>;

    @Select(ModulesSelectors.getModuleConfigText('auth'))
    moduleAuthTexts$!: Observable<any>;

    @Output()
    public changeScreenEvent = new EventEmitter();

    private subscriptions: Subscription[] = [];

    public emailFormExpand = false;
    public socialsProvidersInit = false;
    public isEventSent = false;

    /**
     * contain loginForm instance
     * @type {formGroup}
     */
    public loginForm!: FormGroup;

    public isFormExpand = false;
    public isFormInit = false;

    public formSubmitted = false;
    private loginRequestInProcess = false; // ! This is fix for the login with fingerprint on samsung device
    public formErrorMessage = '';
    public showLocalLoader = false;
    public socialProviders: any = [];

    public authModuleTexts: any;

    public documentDirection = '';

    /**
     * Contain the component basic settings
     */
    public componentSettings = {
        msg:
            'Before we can continue, please check the information you have provided in these fields:',
        errorMessages: [],
        form: {
            fields: {
                email: {
                    textFieldSettings: {
                        type: 'text',
                        label: 'E-mail',
                        placeholder: 'E-mail',
                        validate: true,
                        value: '',
                        errMsg: 'Please enter valid e-mail address.',
                    },
                },
                password: {
                    textFieldSettings: {
                        type: 'password',
                        label: 'Password',
                        placeholder: 'Password',
                        validate: true,
                        value: '',
                        errMsg: 'Password is required.',
                        showPassword: false,
                    },
                },
                loggedIn: {
                    textFieldSettings: {
                        label: 'Keep me logged in',
                    },
                },
                login: {
                    label: 'Sign in',
                },
            },
            labels: {
                signUp: {
                    label: 'or',
                },
            },
            navLinks: {
                resetPassword: {
                    label: 'Forgot password?',
                },
                createAccount: {
                    label: 'Create account',
                },
            },
        },
    };
    
    constructor(private fb: FormBuilder, private store: Store, private _validationService: ValidationsService, private loaderService: SosLoaderService) {
        
    }

    ngOnInit(): void {

        this.store.select(LocalizationSelectors.getCurrentDirection()).subscribe(
            (currentDirection) => {
                if (currentDirection) {
                    this.documentDirection = currentDirection;
                }
            }
        )

        this.authModuleTexts = this.store.selectSnapshot(ModulesSelectors.getModuleConfigText('auth'));

        const disableAuthLoginOptions = this.store.selectSnapshot(
            ModulesSelectors.getSetting('disable_auth_login_options')
        );

        const initRouteData = this.store.selectSnapshot(NavigationSelectors.getInitRoute);
        
        // Auto Login
        if (initRouteData?.data?.auto_login_token && initRouteData?.data?.username) {
            const loader = this.loaderService.showLoader();
            this.store
                .dispatch(new AuthorizationActions.Login({ auto_login_token: initRouteData.data.auto_login_token }))
                .subscribe(
                    (res) => {
                        // on success login it will automaticly redirect you to dashboard
                    },
                    (errorResponse) => {

                        this.formErrorMessage = errorResponse.error
                        ? errorResponse?.error[0]?.message
                        : 'Unable to sign in. Please try again later or use a different method to sign in.';

                        //..
                        this.loaderService.hideLoader(loader);
                    }
                );
        }

        // Wait for patterns then render the form
        this.subscriptions.push(this.store.select(ValidationsSelectors.getPatterns())
            .subscribe((patterns) => {
                this.isFormInit = true;
                this.loginForm = this.fb.group({
                    email: ['', [Validators.required, Validators.pattern(this._validationService.getPattern('register', 'email'))]],
                    password: ['', Validators.required],
                    loggedIn: [''],
                });
            }));

        this.socialProviders = this.store.selectSnapshot(AuthorizationSelectors.getSocialProviders);

        // filter social providers
        if (disableAuthLoginOptions) {
            this.socialProviders = this.socialProviders.filter((item) => disableAuthLoginOptions?.indexOf(item.type?.toLowerCase()) === -1);
        }
        

        if (!this.socialProviders || !(this.socialProviders && this.socialProviders.length > 0)) {

            const sp = this.store.dispatch(AuthorizationActions.GetSocialProviders)
            .subscribe(
                (data) => {
                    this.socialProviders = this.store.selectSnapshot(AuthorizationSelectors.getSocialProviders);

                    // filter social providers
                    if (disableAuthLoginOptions) {
                        this.socialProviders = this.socialProviders.filter((item) => disableAuthLoginOptions?.indexOf(item.type?.toLowerCase()) === -1);
                    }

                    this.socialsProvidersInit = true;
                    this.isFormExpand = true;
                }
            )
        } else {
            this.socialsProvidersInit = true;
        }
    }

    get email() {
        return this.loginForm.get('email');
    }

    get password() {
        return this.loginForm.get('password');
    }

    get logginIn() {
        return this.loginForm.get('loggedIn');
    }

    public login(data) {
        
        switch (data.type) {
            case 'Facebook':
            case 'Apple':
            case 'Google':
            case 'Microsoft': {
                this.loginWithSocial(data);
                break;
            }
            case 'Email': {
                this.loginWithEmail(data);
                //statements;
                break;
            }
        }
    }

    // Check form email login option in social provers
    public hasEmailSocialLogin(): boolean {
        // Remove this checker when email with login comes only from social providers !!!
        const emailOption = this.socialProviders?.length ? this.socialProviders.filter((provider) => provider.type === 'Email') : [];

        return !!emailOption.length;
    }

    public loginSocial(data) {

        this.showLocalLoader = true;

        this.store
            .dispatch(new AuthorizationActions.SocialSingIn(data))
            .pipe(
                finalize(()=> {
                    this.getAuthorizeUserData(data);
                    this.showLocalLoader = false;
                })
            )
            .subscribe(
                (response) => {
                    this.formErrorMessage = '';
                },
                (errorResponse) => {
                    
                    this.formErrorMessage = errorResponse.error
                        ? errorResponse?.error[0]?.message
                        : this.authModuleTexts?.error_social_authorization_fail ? this.authModuleTexts.error_social_authorization_fail : 'Unable to sign in. Please try again later or use a different method to sign in.';
                    
                    setTimeout(() => {
                        try {
                            const modalElement: HTMLElement | null = document.querySelector('.authentication-modal-box');

                            if (modalElement) modalElement.scrollIntoView({ behavior: 'smooth' });
                        } catch (err) {
                            // selector didnt work
                        }
                    }, 1)
                }
            );
    }

    /**
     * Login with Azure B2C
     */
    private loginWithSocial(data): void {

        this.showLocalLoader = true;

        this.store
            .dispatch(new AuthorizationActions.SocialSingIn(data))
            .pipe(
                finalize(()=> {
                    this.showLocalLoader = false;
                })
            )
            .subscribe(
                (response) => {
                    this.getAuthorizeUserData(data);
                },
                (error) => {

                }
            );
    }

    /**
     * Process login form. Get user data.
     */
    private loginWithEmail(formData): void {
        if (this.loginRequestInProcess) {
            // ! Fix for samsung fingerprint login
            return;
        }
        this.loginRequestInProcess = true;
        this.showLocalLoader = true;

        this.store
            .dispatch(new AuthorizationActions.Login(formData.data))
            .pipe(
                finalize(() => {
                    this.loginRequestInProcess = false;
                    // this.showLocalLoader = false;
                })
            )
            .subscribe(
                (response) => {
                    this.formErrorMessage = '';
                    this.getAuthorizeUserData({type: 'standard'});
                },
                (errorResponse) => {
                    // Hide local loader on error, if success then navigate to dahsboard will clear the local loader
                    this.showLocalLoader = false;

                    this.formErrorMessage = errorResponse.error
                        ? errorResponse?.error[0]?.message
                        : 'Unable to sign in. Please try again later or use a different method to sign in.';

                    setTimeout(() => {
                        try {
                            const modalElement: HTMLElement | null = document.querySelector('.authentication-modal-box');

                            if (modalElement) modalElement.scrollIntoView({ behavior: 'smooth' });
                        } catch (err) {
                            // selector didnt work
                        }
                    }, 1)
                }
            );
    }

    public validateForm(): void {
        // old email login
        if (!this.isFormExpand && !this.hasEmailSocialLogin()) {
            this.isFormExpand = true;
            return;
        } 

        // new email login
        if (!this.emailFormExpand && this.hasEmailSocialLogin()) {
            this.emailFormExpand = true;
            return;
        }

        this.formSubmitted = true;

        if (this.loginForm.valid) {
            const formValue = this.loginForm.value;

            const formData = {
                data: {
                    username: formValue.email,
                    password: formValue.password,
                },
                type: 'Email',
            };
            this.login(formData);
        } else {
            // front end valiation handle it
        }
    }

    private getAuthorizeUserData(loginData?) {
        this.store.dispatch(new AccountActions.Fetch())
        .pipe(
            finalize(
                () => {
                    // ..
                }
            )
        )
        .subscribe(
            (success) => {
                this.subscriptions.push(this.store.select(AccountSelectors.account)
                    .subscribe(
                        (data) => {

                            const eventData = {
                                type: loginData?.type,
                                membership: data?.membership,
                                user_id: data?.id
                            }
                    
                            if (!this.isEventSent && data) {
                                this.store.dispatch(
                                    new TrackingActions.Push('login', eventData)
                                );

                                this.isEventSent = true;
                            }
                        }
                    ));
            },
            (error) => {
                // Hide local loader on error, if success then navigate to dashboard will clear the local loader
                this.showLocalLoader = false;
            }
        );
    }

    /**
     * Reset password handler
     */
     public resetPasswordHandler(screen): void {

        this.changeScreenEvent.emit(screen);

    }

    ngOnDestroy(): void {
        this.subscriptions.map((el) => el.unsubscribe());
    }
}
